/**
 * Achievement Manager
 * Handles achievement unlocking and checking
 */

import { doc, updateDoc, arrayUnion, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { getAllAchievements } from '../data/achievementDefinitions';

/**
 * Check if user meets achievement requirement
 * @param {object} achievement - Achievement definition
 * @param {object} userStats - User stats from Firestore
 * @returns {boolean} - True if requirement is met
 */
export const checkAchievementRequirement = (achievement, userStats) => {
  const { requirement } = achievement;
  const stats = userStats.stats || {};

  switch (requirement.type) {
    case 'shifts_completed':
      return (stats.totalShifts || 0) >= requirement.value;
    
    case 'inspections_completed':
      return (stats.totalInspections || 0) >= requirement.value;
    
    case 'login_streak':
      return (stats.longestStreak || 0) >= requirement.value;
    
    case 'early_shifts':
      return (stats.earlyShifts || 0) >= requirement.value;
    
    case 'night_shifts_completed':
      return (stats.nightShiftsCompleted || 0) >= requirement.value;
    
    case 'issues_resolved':
      return (stats.issuesResolved || 0) >= requirement.value;
    
    case 'team_activities':
      return (stats.teamActivities || 0) >= requirement.value;
    
    default:
      return false;
  }
};

/**
 * Check and unlock achievements for a user
 * @param {string} userId - User ID
 * @param {object} userStats - User stats
 * @param {function} addTCoins - Function to add T Coins
 * @returns {Promise<Array>} - Array of unlocked achievement IDs
 */
export const checkAndUnlockAchievements = async (userId, userStats, addTCoins = null) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return [];
    }

    const userData = userSnap.data();
    const currentAchievements = userData.achievements || [];
    const allAchievements = getAllAchievements();
    const unlockedAchievements = [];

    // Check each achievement
    for (const achievement of allAchievements) {
      // Skip if already unlocked
      if (currentAchievements.includes(achievement.achievementId)) {
        continue;
      }

      // Check requirement
      const meetsRequirement = checkAchievementRequirement(achievement, {
        ...userData,
        stats: userData.stats || {}
      });

      if (meetsRequirement) {
        // Unlock achievement
        await updateDoc(userRef, {
          achievements: arrayUnion(achievement.achievementId),
          updatedAt: serverTimestamp()
        });

        // Award achievement rewards
        if (achievement.reward.points > 0 || achievement.reward.xp > 0 || achievement.reward.coins > 0) {
          const currentPoints = userData.totalPoints || 0;
          const currentXP = userData.currentXP || 0;
          const totalXP = userData.totalXP || 0;

          await updateDoc(userRef, {
            totalPoints: currentPoints + achievement.reward.points,
            currentXP: currentXP + achievement.reward.xp,
            totalXP: totalXP + achievement.reward.xp,
            updatedAt: serverTimestamp()
          });

          // Add coins if provided
          if (achievement.reward.coins > 0 && addTCoins) {
            await addTCoins(userId, 'virtual', achievement.reward.coins, `รางวัล Achievement: ${achievement.nameThai}`);
          }
        }

        unlockedAchievements.push(achievement.achievementId);
      }
    }

    return unlockedAchievements;
  } catch (error) {
    console.error('Error checking and unlocking achievements:', error);
    return [];
  }
};

/**
 * Get user's unlocked achievements
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of achievement IDs
 */
export const getUserAchievements = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return [];
    }

    const userData = userSnap.data();
    return userData.achievements || [];
  } catch (error) {
    console.error('Error getting user achievements:', error);
    return [];
  }
};

